import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import {
  AutoFixHigh as GenerateIcon,
  Clear as ClearIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
} from "@mui/icons-material";
import { OrgThemeBackgroundLevels } from "../../../common/types";
import { isValidHex } from "../../../theme/paletteGenerator";
import ColorInput from "./ColorInput";

interface BackgroundLevelsEditorProps {
  value: OrgThemeBackgroundLevels;
  onChange: (v: OrgThemeBackgroundLevels) => void;
  disabled?: boolean;
  mode?: "light" | "dark";
}

const PLACEHOLDERS = {
  light: { level1: "#ebebef", level2: "#e0e0e5", level3: "#d5d5db" },
  dark:  { level1: "#2a2a35", level2: "#32323f", level3: "#3a3a48" },
};

function blendHex(base: string, target: string, t: number): string {
  const r1 = parseInt(base.slice(1, 3), 16);
  const g1 = parseInt(base.slice(3, 5), 16);
  const b1 = parseInt(base.slice(5, 7), 16);
  const r2 = parseInt(target.slice(1, 3), 16);
  const g2 = parseInt(target.slice(3, 5), 16);
  const b2 = parseInt(target.slice(5, 7), 16);
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

const LEVEL_ROWS = [
  { field: "level1" as keyof OrgThemeBackgroundLevels, label: "Level 1" },
  { field: "level2" as keyof OrgThemeBackgroundLevels, label: "Level 2" },
  { field: "level3" as keyof OrgThemeBackgroundLevels, label: "Level 3" },
];

const BackgroundLevelsEditor: React.FC<BackgroundLevelsEditorProps> = ({
  value,
  onChange,
  disabled,
  mode = "light",
}) => {
  const placeholders = PLACEHOLDERS[mode];
  const hasOverrides = !!(value.level1 || value.level2 || value.level3);
  const [baseHex, setBaseHex] = useState(value.level1 ?? "");
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setBaseHex(value.level1 ?? "");
  }, [value.level1]);

  const handleGenerate = () => {
    const hex = baseHex.trim().startsWith("#") ? baseHex.trim() : `#${baseHex.trim()}`;
    if (!isValidHex(hex)) {
      setGenerateError("Enter a valid hex colour (e.g. #ebebef)");
      return;
    }
    setGenerateError(null);
    const target = mode === "light" ? "#000000" : "#ffffff";
    onChange({
      level1: hex,
      level2: blendHex(hex, target, 0.07),
      level3: blendHex(hex, target, 0.14),
    });
    setExpanded(true);
  };

  const handleReset = () => {
    onChange({});
    setBaseHex("");
    setExpanded(false);
    setGenerateError(null);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography level="title-sm" sx={{ fontWeight: 600 }}>
          Background Levels
          {hasOverrides && (
            <Box
              component="span"
              sx={{
                ml: 1, px: 0.75, py: 0.1, borderRadius: "10px",
                bgcolor: "primary.100", color: "primary.600",
                fontSize: "10px", fontWeight: 600, verticalAlign: "middle",
              }}
            >
              custom
            </Box>
          )}
        </Typography>
        {hasOverrides && (
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            onClick={() => setExpanded((e) => !e)}
            endDecorator={expanded ? <CollapseIcon /> : <ExpandIcon />}
          >
            {expanded ? "Hide levels" : "Edit levels"}
          </Button>
        )}
      </Box>

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ flex: 1 }}>
          <ColorInput
            value={baseHex}
            onChange={(hex) => { setBaseHex(hex); setGenerateError(null); }}
            disabled={disabled}
            placeholder={placeholders.level1}
          />
        </Box>
        <Button
          size="sm"
          variant="outlined"
          startDecorator={<GenerateIcon />}
          onClick={handleGenerate}
          disabled={disabled || !baseHex.trim()}
        >
          Generate
        </Button>
        {hasOverrides && (
          <Button size="sm" variant="plain" color="neutral" onClick={handleReset} disabled={disabled}>
            Reset
          </Button>
        )}
      </Stack>

      {generateError && (
        <Alert color="danger" variant="soft" size="sm" sx={{ mt: 1 }}>
          {generateError}
        </Alert>
      )}

      {hasOverrides && (
        <Box sx={{ display: "flex", mt: 1, borderRadius: "6px", overflow: "hidden", height: 20 }}>
          {LEVEL_ROWS.map(({ field }) => (
            <Box key={field} sx={{ flex: 1, bgcolor: value[field] ?? "transparent" }} />
          ))}
        </Box>
      )}

      {hasOverrides && expanded && (
        <Box sx={{ mt: 1 }}>
          <Divider sx={{ mb: 1 }} />
          <Stack spacing={0}>
            {LEVEL_ROWS.map(({ field, label }) => (
              <Box key={field} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
                <Typography
                  level="body-xs"
                  sx={{ minWidth: 60, flexShrink: 0, color: "text.secondary" }}
                >
                  {label}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ColorInput
                    value={value[field] ?? ""}
                    onChange={(hex) => onChange({ ...value, [field]: hex || undefined })}
                    disabled={disabled}
                    placeholder={placeholders[field]}
                  />
                </Box>
                <Tooltip title="Clear (inherit theme default)" placement="top">
                  <span>
                    <IconButton
                      size="sm"
                      variant="plain"
                      color="neutral"
                      disabled={disabled || !value[field]}
                      onClick={() => onChange({ ...value, [field]: undefined })}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

export default BackgroundLevelsEditor;
